import React, { PureComponent, createRef } from 'react';
import SignaturePadLib from 'signature_pad';

function debounce(fn, delay) {
  let timeout;
  return function (...args) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), delay);
  };
}

class SignaturePad extends PureComponent {
  static displayName = 'react-signature-pad-wrapper';

  constructor(props) {
    super(props);
    this.canvasRef = createRef();
    this.state = {
      canvasWidth: 0,
      canvasHeight: 0,
    };

    this.callResizeHandler = debounce(this.handleResize.bind(this), props.debounceInterval || 150);
  }

  componentDidMount() {
    const canvas = this.canvasRef.current;

    if (canvas) {
      if (!this.props.width || !this.props.height) {
        canvas.style.width = '100%';
        window.addEventListener('resize', this.callResizeHandler);
      }

      this.signaturePad = new SignaturePadLib(canvas, this.props.options || {});

      // Wait for layout
      requestAnimationFrame(() => {
        this.scaleCanvas(canvas);
      });
    }
  }

  componentWillUnmount() {
    if (!this.props.width || !this.props.height) {
      window.removeEventListener('resize', this.callResizeHandler);
    }

    if (this.signaturePad) {
      this.signaturePad.off();
    }
  }

  // Public getters/setters
  get instance() {
    return this.signaturePad;
  }

  get canvas() {
    return this.canvasRef;
  }

  set dotSize(dotSize) {
    this.signaturePad.dotSize = dotSize;
  }

  get dotSize() {
    return this.signaturePad.dotSize;
  }

  set minWidth(val) {
    this.signaturePad.minWidth = val;
  }

  get minWidth() {
    return this.signaturePad.minWidth;
  }

  set maxWidth(val) {
    this.signaturePad.maxWidth = val;
  }

  get maxWidth() {
    return this.signaturePad.maxWidth;
  }

  set throttle(val) {
    this.signaturePad.throttle = val;
  }

  get throttle() {
    return this.signaturePad.throttle;
  }

  set backgroundColor(val) {
    this.signaturePad.backgroundColor = val;
  }

  get backgroundColor() {
    return this.signaturePad.backgroundColor;
  }

  set penColor(val) {
    this.signaturePad.penColor = val;
  }

  get penColor() {
    return this.signaturePad.penColor;
  }

  set velocityFilterWeight(val) {
    this.signaturePad.velocityFilterWeight = val;
  }

  get velocityFilterWeight() {
    return this.signaturePad.velocityFilterWeight;
  }

  isEmpty() {
    return this.signaturePad.isEmpty();
  }

  clear() {
    this.signaturePad.clear();
  }

  fromDataURL(dataUrl, options = {}) {
    this.signaturePad.fromDataURL(dataUrl, options);
  }

  toDataURL(type, encoderOptions) {
    return this.signaturePad.toDataURL(type, encoderOptions);
  }

  toSVG(svgOptions) {
    return this.signaturePad.toSVG(svgOptions);
  }

  fromData(data) {
    this.signaturePad.fromData(data);
  }

  toData() {
    return this.signaturePad.toData();
  }

  off() {
    this.signaturePad.off();
  }

  on() {
    this.signaturePad.on();
  }

  handleResize() {
    const canvas = this.canvasRef.current;
    if (canvas) {
      this.scaleCanvas(canvas);
    }
  }

  scaleCanvas(canvas) {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = (this.props.width || canvas.offsetWidth) * ratio;
    const height = (this.props.height || canvas.offsetHeight) * ratio;

    const { canvasWidth, canvasHeight } = this.state;

    if (width === canvasWidth && height === canvasHeight) return;

    let data;
    if (this.props.redrawOnResize && this.signaturePad && !this.signaturePad.isEmpty()) {
      data = this.signaturePad.toDataURL();
    }

    canvas.width = width;
    canvas.height = height;
    this.setState({ canvasWidth: width, canvasHeight: height });

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
    }

    if (data) {
      this.signaturePad.fromDataURL(data);
    } else {
      this.signaturePad.clear();
    }
  }

  render() {
    const { canvasProps = {} } = this.props;
    return <canvas data-testid="canvas-element" ref={this.canvasRef} {...canvasProps} />;
  }
}

export default SignaturePad;
